import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CalendarView = ({ entries }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const getSeverityColor = (severity) => {
    if (severity >= 8) return 'bg-error text-white';
    if (severity >= 6) return 'bg-error/60 text-white';
    if (severity >= 4) return 'bg-warning text-white';
    if (severity >= 2) return 'bg-warning/60 text-foreground';
    return 'bg-success text-white';
  };

  const getSeverityLabel = (severity) => {
    if (severity >= 8) return 'Very Severe';
    if (severity >= 6) return 'Severe';
    if (severity >= 4) return 'Moderate';
    if (severity >= 2) return 'Mild';
    return 'Very Mild';
  };

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getEntriesForDate = (date) => {
    return entries?.filter(entry => {
      const entryDate = new Date(entry.date);
      return (entryDate?.getDate() === date?.getDate() &&
      entryDate?.getMonth() === date?.getMonth() && entryDate?.getFullYear() === date?.getFullYear());
    });
  };

  const getMaxSeverityForDate = (date) => {
    const dateEntries = getEntriesForDate(date);
    if (dateEntries?.length === 0) return null;
    return Math.max(...dateEntries?.map(e => e?.severity));
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);

  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
    setSelectedDate(null);
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
    setSelectedDate(null);
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(year, month, day);
    setSelectedDate(clickedDate);
  };

  const selectedDateEntries = selectedDate ? getEntriesForDate(selectedDate) : [];

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border p-4 md:p-6">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={previousMonth}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Previous month"
          >
            <Icon name="ChevronLeft" size={20} />
          </button>
          <h3 className="text-lg md:text-xl font-heading font-bold text-foreground">
            {monthNames?.[month]} {year}
          </h3>
          <button
            onClick={nextMonth}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Next month"
          >
            <Icon name="ChevronRight" size={20} />
          </button>
        </div>

        {/* Day Names */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {dayNames?.map(day => (
            <div key={day} className="text-center text-xs md:text-sm font-medium text-muted-foreground py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">
          {/* Empty cells for days before month starts */}
          {Array.from({ length: startingDayOfWeek })?.map((_, index) => (
            <div key={`empty-${index}`} className="aspect-square" />
          ))}

          {/* Days of the month */}
          {Array.from({ length: daysInMonth })?.map((_, index) => {
            const day = index + 1;
            const date = new Date(year, month, day);
            const maxSeverity = getMaxSeverityForDate(date);
            const dateEntries = getEntriesForDate(date);
            const isSelected = selectedDate && 
              selectedDate?.getDate() === day && 
              selectedDate?.getMonth() === month && 
              selectedDate?.getFullYear() === year;
            const isToday = 
              new Date()?.getDate() === day && 
              new Date()?.getMonth() === month && 
              new Date()?.getFullYear() === year;

            return (
              <button
                key={day}
                onClick={() => handleDateClick(day)}
                className={`
                  aspect-square rounded-lg p-1 md:p-2 text-sm md:text-base font-medium
                  transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-ring
                  ${
                    maxSeverity !== null
                      ? getSeverityColor(maxSeverity)
                      : 'bg-muted/30 text-muted-foreground hover:bg-muted'
                  }
                  ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
                  ${isToday ? 'font-bold' : ''}
                `}
                aria-label={`${day} ${monthNames?.[month]} ${year}${dateEntries?.length > 0 ? ` - ${dateEntries?.length} entries` : ''}`}
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <span>{day}</span>
                  {dateEntries?.length > 0 && (
                    <span className="text-xs opacity-75">{dateEntries?.length}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-6 pt-4 border-t border-border">
          <p className="text-xs font-medium text-muted-foreground mb-3">Severity Legend:</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[
              { severity: 1, label: 'Very Mild' },
              { severity: 3, label: 'Mild' },
              { severity: 5, label: 'Moderate' },
              { severity: 7, label: 'Severe' },
              { severity: 9, label: 'Very Severe' }
            ]?.map(item => (
              <div key={item?.severity} className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded ${getSeverityColor(item?.severity)}`} />
                <span className="text-xs text-muted-foreground">{item?.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Selected Date Details */}
      {selectedDate && selectedDateEntries?.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-heading font-bold text-foreground">
              {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </h4>
            <button
              onClick={() => setSelectedDate(null)}
              className="p-1 hover:bg-muted rounded-lg transition-colors"
              aria-label="Close details"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
          <div className="space-y-3">
            {selectedDateEntries?.map(entry => (
              <div key={entry?.id} className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h5 className="font-medium text-foreground">{entry?.symptomType}</h5>
                    <p className="text-xs text-muted-foreground">
                      {new Date(entry.date)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(entry?.severity)}`}>
                    {getSeverityLabel(entry?.severity)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{entry?.description}</p>
                {entry?.location && (
                  <div className="flex items-center gap-1 mt-2">
                    <Icon name="MapPin" size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{entry?.location}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {selectedDate && selectedDateEntries?.length === 0 && (
        <div className="bg-card rounded-xl border border-border p-6 text-center">
          <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">
            No symptoms recorded for {selectedDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      )}
    </div>
  );
};

export default CalendarView;